import Control from "ol/control/Control";

/**
 * Renders a per-layerId revision picker inside the VectorFeatureControl row.
 *
 * The toggle icon is appended into `options.headerEl` (inline with the label).
 * The panel with revision rows is inserted into `options.bodyEl` before the
 * attributes table, so it flows naturally in the document when expanded.
 *
 * Fires "selectedRevisions" property change when any value changes.
 * Fires "refreshRequested" property change when the refresh button is clicked.
 */
export class RevisionControl extends Control {
  /**
   * @param {{ headerEl: HTMLElement, bodyEl: HTMLElement }} options
   */
  constructor(options = {}) {
    // OL Control needs an element — use a hidden placeholder
    const placeholder = document.createElement("span");
    super({ element: placeholder, target: options.headerEl });

    // ── Toggle button (icon) — lives inline in the header ──────────────────
    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "revision-toggle-btn";
    toggleBtn.title = "Show revisions";
    const icon = document.createElement("img");
    icon.src = "history.png";
    icon.alt = "Revisions";
    icon.className = "revision-toggle-icon";
    toggleBtn.appendChild(icon);

    // Loading indicator shown inside the button until revisions arrive
    const statusEl = document.createElement("span");
    statusEl.className = "revision-loading";
    statusEl.title = "Loading revisions…";
    statusEl.textContent = "…";
    toggleBtn.appendChild(statusEl);

    // ── Panel — lives in the body, flows inline below the header ───────────
    const panel = document.createElement("div");
    panel.className = "revision-panel";
    panel.hidden = true;

    toggleBtn.addEventListener("click", () => {
      panel.hidden = !panel.hidden;
      toggleBtn.classList.toggle("revision-toggle-btn--open", !panel.hidden);
      toggleBtn.title = panel.hidden ? "Show revisions" : "Hide revisions";
    });

    // Append the icon to the header; insert panel before attributes table
    if (options.headerEl) {
      options.headerEl.appendChild(toggleBtn);
    }
    if (options.bodyEl) {
      // Insert after the header div (first child), before anything else
      const header = options.bodyEl.querySelector(".vector-feature-header");
      if (header && header.nextSibling) {
        options.bodyEl.insertBefore(panel, header.nextSibling);
      } else {
        options.bodyEl.appendChild(panel);
      }
    }

    this._toggleBtn = toggleBtn;
    this._statusEl = statusEl;
    this._panel = panel;
    this._revisions = [];
    this._refreshBtn = null;
  }

  setRevisions(layerRevisions) {
    this._revisions = layerRevisions.map((lr) => ({ ...lr }));
    this._statusEl.remove();
    this._render();
  }

  setError(err) {
    this._statusEl.className = "revision-error";
    this._statusEl.textContent = " ⚠";
    this._statusEl.title = "";
    const detail = err?.message ?? "Failed to load revisions";
    this._panel.innerHTML = "";
    const msg = document.createElement("p");
    msg.className = "revision-error";
    msg.textContent = `Revisions unavailable — ${detail}`;
    this._panel.appendChild(msg);
  }

  setLoading() {
    if (this._refreshBtn) {
      this._refreshBtn.disabled = true;
      this._refreshBtn.textContent = "Updating…";
    }
  }

  expand() {
    this._panel.hidden = false;
    this._toggleBtn.classList.add("revision-toggle-btn--open");
    this._toggleBtn.title = "Hide revisions";
  }

  // ── private ────────────────────────────────────────────────────────────────

  _render() {
    this._panel.innerHTML = "";

    if (this._revisions.length === 0) return;

    this._revisions.forEach((lr) => {
      const row = document.createElement("div");
      row.className = "revision-row";

      const label = document.createElement("span");
      label.className = "revision-layer-id";
      label.textContent = `layer ${lr.layerId}`;

      const minusBtn = document.createElement("button");
      minusBtn.className = "revision-btn";
      minusBtn.type = "button";
      minusBtn.textContent = "−";

      const input = document.createElement("input");
      input.type = "number";
      input.className = "revision-input";
      input.value = String(lr.revision);
      input.min = "0";

      const plusBtn = document.createElement("button");
      plusBtn.className = "revision-btn";
      plusBtn.type = "button";
      plusBtn.textContent = "+";

      minusBtn.addEventListener("click", () => {
        const current = parseInt(input.value, 10) || 0;
        if (current > 0) input.value = String(current - 1);
        this._updateRevision(lr, input);
      });

      plusBtn.addEventListener("click", () => {
        const current = parseInt(input.value, 10) || 0;
        input.value = String(current + 1);
        this._updateRevision(lr, input);
      });

      input.addEventListener("change", () => this._updateRevision(lr, input));

      row.appendChild(label);
      row.appendChild(minusBtn);
      row.appendChild(input);
      row.appendChild(plusBtn);
      this._panel.appendChild(row);
    });

    const refreshBtn = document.createElement("button");
    refreshBtn.className = "revision-refresh-btn";
    refreshBtn.type = "button";
    refreshBtn.textContent = "↺ Update to latest revisions";
    refreshBtn.title = "Re-fetch the latest revisions from the server";
    refreshBtn.addEventListener("click", () => {
      this.set("refreshRequested", Date.now());
    });
    this._refreshBtn = refreshBtn;
    this._panel.appendChild(refreshBtn);
  }

  _updateRevision(lr, input) {
    lr.revision = parseInt(input.value, 10) || 0;
    this.set(
      "selectedRevisions",
      this._revisions.map((r) => ({ ...r })),
    );
  }
}
